import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface DemoUser {
  id: string;
  email: string;
  full_name: string;
  role: 'citizen' | 'official' | 'admin';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface DemoAuthContextType {
  user: DemoUser | null;
  login: (email: string, password: string) => boolean;
  logout: () => void;
  isAdmin: boolean;
  isAuthenticated: boolean;
  loading: boolean;
}

const DemoAuthContext = createContext<DemoAuthContextType | null>(null);

export const useDemoAuth = () => {
  const context = useContext(DemoAuthContext);
  if (!context) {
    throw new Error('useDemoAuth must be used within a DemoAuthProvider');
  }
  return context;
};

interface DemoAuthProviderProps {
  children: ReactNode;
}

export const DemoAuthProvider: React.FC<DemoAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<DemoUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in from localStorage
    const savedUser = localStorage.getItem('demoUser');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('demoUser');
      }
    }
    setLoading(false);
  }, []);

  const login = (email: string, password: string): boolean => {
    // Demo credentials
    if (email === 'admin@gov.in' && password === 'admin123') {
      const adminUser: DemoUser = {
        id: 'demo-admin-1',
        email: 'admin@gov.in',
        full_name: 'Admin User',
        role: 'admin',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setUser(adminUser);
      localStorage.setItem('demoUser', JSON.stringify(adminUser));
      return true;
    } else if (email === 'user@gmail.com' && password === 'user123') {
      const citizenUser: DemoUser = {
        id: 'demo-citizen-1',
        email: 'user@gmail.com',
        full_name: 'Citizen User',
        role: 'citizen',
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setUser(citizenUser);
      localStorage.setItem('demoUser', JSON.stringify(citizenUser));
      return true;
    }
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('demoUser');
  };

  const value = {
    user,
    login,
    logout,
    isAdmin: user?.role === 'admin',
    isAuthenticated: !!user,
    loading
  };

  return (
    <DemoAuthContext.Provider value={value}>
      {children}
    </DemoAuthContext.Provider>
  );
};